import React, { forwardRef, useCallback, useRef } from 'react'

import Box from '../box'
import {
  coloring,
  composeGroups,
  createView,
  flexContainer,
  flexItem,
  layout,
  sizing,
  spacing,
  type KitViewProps,
  type LayoutEvent
} from '../_support'

/* -----------------------------------------------------------------------------
 * ScrollView
 *
 * react-native's ScrollView on the web: a scrolling View around a content
 * View. Style props size the scrolling View; `contentContainerStyle` styles the
 * content. The ref is the scrolling node with scrollTo, scrollToEnd and
 * getScrollableNode, and onScroll fires at scroll start and end, or every
 * `scrollEventThrottle` ms while scrolling.
 * -------------------------------------------------------------------------- */

export const scrollViewStyles = composeGroups(
  coloring,
  flexContainer,
  flexItem,
  layout,
  sizing,
  spacing
)

const ScrollRoot = createView({
  displayName: 'ScrollViewRoot',
  groups: scrollViewStyles
})

// react-native-web's registered ScrollView styles
const common = {
  flexGrow: 1,
  flexShrink: 1,
  transform: [{ translateZ: 0 }],
  WebkitOverflowScrolling: 'touch'
}
const baseVertical = {
  ...common,
  flexDirection: 'column',
  overflowX: 'hidden',
  overflowY: 'auto'
}
const baseHorizontal = {
  ...common,
  flexDirection: 'row',
  overflowX: 'auto',
  overflowY: 'hidden'
}
const stickyHeader = { position: 'sticky', top: 0, zIndex: 10 }

export type ScrollEvent = {
  nativeEvent: {
    contentOffset: { x: number; y: number }
    contentSize: { width: number; height: number }
    layoutMeasurement: { width: number; height: number }
  }
  timeStamp: number
}

export type ScrollViewProps = KitViewProps & {
  horizontal?: boolean
  contentContainerStyle?: any
  onScroll?: (event: ScrollEvent) => void
  scrollEventThrottle?: number
  scrollEnabled?: boolean
  showsVerticalScrollIndicator?: boolean
  showsHorizontalScrollIndicator?: boolean
  stickyHeaderIndices?: number[]
  onContentSizeChange?: (width: number, height: number) => void
  keyboardShouldPersistTaps?: unknown
  keyboardDismissMode?: unknown
}

export type ScrollViewNode = HTMLElement & {
  getScrollableNode: () => HTMLElement
  getInnerViewNode: () => HTMLElement | null
  scrollTo: (
    options?: { x?: number; y?: number; animated?: boolean } | number
  ) => void
  scrollToEnd: (options?: { animated?: boolean }) => void
  flashScrollIndicators: () => void
}

const normalizeScrollEvent = (target: HTMLElement): ScrollEvent => ({
  nativeEvent: {
    contentOffset: { x: target.scrollLeft, y: target.scrollTop },
    contentSize: { width: target.scrollWidth, height: target.scrollHeight },
    layoutMeasurement: {
      width: target.offsetWidth,
      height: target.offsetHeight
    }
  },
  timeStamp: Date.now()
})

const ScrollView = forwardRef<ScrollViewNode, ScrollViewProps>(
  (
    {
      children,
      horizontal = false,
      contentContainerStyle,
      onScroll,
      scrollEventThrottle = 0,
      scrollEnabled = true,
      showsVerticalScrollIndicator,
      showsHorizontalScrollIndicator,
      stickyHeaderIndices,
      onContentSizeChange,
      keyboardShouldPersistTaps,
      keyboardDismissMode,
      ...props
    },
    forwardedRef
  ) => {
    const scrollNode = useRef<ScrollViewNode | null>(null)
    const innerNode = useRef<HTMLElement | null>(null)
    const scrollState = useRef({ isScrolling: false, lastTick: 0 })
    const scrollEnd = useRef<ReturnType<typeof setTimeout>>()

    const setScrollNode = useCallback(
      (node: ScrollViewNode | null) => {
        scrollNode.current = node
        if (node) {
          node.getScrollableNode = () => node
          node.getInnerViewNode = () => innerNode.current
          node.scrollTo = (options = {}) => {
            const {
              x = 0,
              y = 0,
              animated = true
            }: {
              x?: number
              y?: number
              animated?: boolean
            } = typeof options === 'number' ? { y: options } : options
            node.scroll({
              left: x,
              top: y,
              behavior: animated ? 'smooth' : 'auto'
            })
          }
          node.scrollToEnd = ({ animated = true } = {}) =>
            node.scroll({
              left: horizontal ? node.scrollWidth : 0,
              top: horizontal ? 0 : node.scrollHeight,
              behavior: animated ? 'smooth' : 'auto'
            })
          node.flashScrollIndicators = () => undefined
        }
        if (typeof forwardedRef === 'function') forwardedRef(node)
        else if (forwardedRef) forwardedRef.current = node
      },
      [forwardedRef, horizontal]
    )

    const emit = (target: HTMLElement) =>
      onScroll?.(normalizeScrollEvent(target))
    const handleScroll = (event: React.UIEvent<HTMLElement>) => {
      event.stopPropagation()
      if (event.target !== scrollNode.current) return
      const target = event.target as HTMLElement
      clearTimeout(scrollEnd.current)
      scrollEnd.current = setTimeout(() => {
        scrollState.current.isScrolling = false
        emit(target)
      }, 100)
      if (scrollState.current.isScrolling) {
        const sinceLast = Date.now() - scrollState.current.lastTick
        if (scrollEventThrottle > 0 && sinceLast >= scrollEventThrottle) {
          scrollState.current.lastTick = Date.now()
          emit(target)
        }
      } else {
        scrollState.current.isScrolling = true
        scrollState.current.lastTick = Date.now()
        emit(target)
      }
    }

    const handleContentLayout = onContentSizeChange
      ? (event: LayoutEvent) =>
          onContentSizeChange(
            event.nativeEvent.layout.width,
            event.nativeEvent.layout.height
          )
      : undefined

    const content = stickyHeaderIndices?.length
      ? React.Children.map(children, (child, index) =>
          stickyHeaderIndices.includes(index) ? (
            <Box style={stickyHeader}>{child}</Box>
          ) : (
            child
          )
        )
      : children

    const hideScrollbar =
      showsHorizontalScrollIndicator === false ||
      showsVerticalScrollIndicator === false

    return (
      <ScrollRoot
        ref={setScrollNode as any}
        {...props}
        onScroll={handleScroll}
        kitLayers={{
          classes: horizontal ? baseHorizontal : baseVertical,
          inline: {
            ...(scrollEnabled
              ? {}
              : {
                  overflowX: 'hidden',
                  overflowY: 'hidden',
                  touchAction: 'none'
                }),
            ...(hideScrollbar ? { scrollbarWidth: 'none' } : {})
          }
        }}
      >
        <Box
          ref={innerNode as any}
          onLayout={handleContentLayout}
          style={[
            horizontal && { flexDirection: 'row' },
            contentContainerStyle
          ]}
        >
          {content}
        </Box>
      </ScrollRoot>
    )
  }
)
ScrollView.displayName = 'ScrollView'

export default ScrollView
